import PropTypes from "prop-types";
import { createContext, useEffect, useReducer, useCallback, useMemo } from "react";
// utils
import axios from "../utils/axios";
import localStorageAvailable from "../utils/localStorageAvailable";
import { useNavigate } from "react-router-dom";
//
import { isValidToken, setSession, setRefresh, setLevel } from "./utils";
const backendUrl = process.env.REACT_APP_BACKEND_URL;

const initialState = {
  isInitialized: false,
  isAuthenticated: false,
  user: null,
  paymentMethods: null,
};

const reducer = (state, action) => {
  if (action.type === "INITIAL") {
    return {
      isInitialized: true,
      isAuthenticated: action?.payload?.isAuthenticated,
      user: action?.payload?.user,
      paymentMethods: action?.payload?.paymentMethods?.results,
    };
  }
  if (action.type === "LOGIN") {
    return {
      ...state,
      isAuthenticated: true,
      user: action?.payload?.user,
      paymentMethods: action?.payload?.paymentMethods?.results,
    };
  }
  if (action.type === "REGISTER") {
    return {
      ...state,
      isAuthenticated: true,
      user: action?.payload?.user,
      paymentMethods: action?.payload?.paymentMethods?.results,
    };
  }

  if (action.type === "RESET") {
    return {
      ...state,
      isAuthenticated: true,
      user: action?.payload?.user,
    };
  }

  if (action.type === "LOGOUT") {
    return {
      ...state,
      isAuthenticated: false,
      user: null,
      paymentMethods: null,
    };
  }
  if (action.type === "UPDATE_USER") {
    return {
      ...state,
      user: action?.payload?.user,
    };
  }
  if (action.type === "UPDATE_PAYMENT_METHOD") {
    return {
      ...state,
      paymentMethods: action?.payload?.paymentMethods,
    };
  }
  if (action.type === "UPDATE_FRANCHISE") {
    return {
      ...state,
      user: {
        ...state.user,
        franchiseId: action.payload.franchiseId,
      },
    };
  }
  if (action.type === "DELETE_PAYMENT_METHOD") {
    return {
      ...state,
      paymentMethods: action?.payload?.paymentMethods,
    };
  }

  return state;
};

export const AuthContext = createContext(null);

AuthProvider.propTypes = {
  children: PropTypes.node,
};

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const navigate = useNavigate();
  const storageAvailable = localStorageAvailable();

  const initialize = useCallback(async () => {
    try {
      const refreshToken = storageAvailable ? localStorage.getItem("refreshToken") : "";
      const accessToken = storageAvailable ? localStorage.getItem("accessToken") : "";

      if (refreshToken && isValidToken(accessToken)) {
        setSession(accessToken);
        setRefresh(refreshToken);

        const response = await axios.post(`${backendUrl}/authf/get-profile`, {
          refreshToken,
        });

        const { user, paymentMethods } = response.data;

        if (user?.franchiseId?.levels?.length) {
          if (!localStorage.getItem("levelSelected")) {
            setLevel(user?.franchiseId?.levels[0]);
          }
        }

        user.displayName = `${user?.firstName} ${user?.lastName} `;
        if (user?.franchiseId?.status === "inActive") {
          dispatch({
            type: "INITIAL",
            payload: {
              isAuthenticated: false,
              user: null,
              paymentMethods: null,
            },
          });
        } else {
          dispatch({
            type: "INITIAL",
            payload: {
              isAuthenticated: true,
              user,
              paymentMethods,
            },
          });
        }
      } else {
        dispatch({
          type: "INITIAL",
          payload: {
            isAuthenticated: false,
            user: null,
            paymentMethods: null,
          },
        });
      }
    } catch (error) {
      console.error(error);
      dispatch({
        type: "INITIAL",
        payload: {
          isAuthenticated: false,
          user: null,
          paymentMethods: null,
        },
      });
    }
  }, [storageAvailable]);

  useEffect(() => {
    initialize();
  }, [initialize]);

  // LOGIN
  const login = useCallback(async (userName, password, ipAddress, browserInfo) => {
    const response = await axios.post(`${backendUrl}/authf/login`, {
      userName,
      password,
      ipAddress,
      browserInfo
    });
    const { tokens, user, paymentMethods } = response.data;

    user.displayName = `${user?.firstName} ${user?.lastName}`;

    setSession(tokens?.access?.token);
    setRefresh(tokens?.refresh?.token);
    dispatch({
      type: "LOGIN",
      payload: {
        user,
        paymentMethods,
      },
    });
    if (user.permissions?.canViewDashboard) {
      navigate("/dashboard/app");
    } else if (user.permissions?.canViewStaff) {
      navigate("/dashboard/user/account");
    } else if (user.permissions?.canAccessReports) {
      navigate("/dashboard/report");
    } else if (user?.permissions?.canViewVenueCustomers) {
      navigate("/dashboard/customer/list");
    } else {
      navigate("/dashboard/settings/new");
    }
  }, []);

  // REGISTER
  const register = useCallback(async (email, password, firstName, lastName) => {
    const response = await axios.post("https://api-dev-minimal-v4.vercel.app/api/account/register", {
      email,
      password,
      firstName,
      lastName,
    });
    const { accessToken, user } = response.data;

    localStorage.setItem("accessToken", accessToken);

    dispatch({
      type: "REGISTER",
      payload: {
        user,
      },
    });
  }, []);

  // LOGOUT
  const logout = useCallback(() => {
    setSession(null);
    dispatch({
      type: "LOGOUT",
    });
  }, []);

  // Reset
  const resetPassword = useCallback(async (old_password, new_password, user) => {
    const response = await axios.patch(`${backendUrl}/franchiseUser/changePassword/${user?.id}`, {
      old_password,
      new_password,
    });

    dispatch({
      type: "RESET",
      payload: {
        user,
      },
    });
    return response;
  }, []);

  const updateUser = useCallback((updatedUser) => {
    updatedUser.displayName = `${updatedUser?.firstName} ${updatedUser?.lastName}`;
    dispatch({
      type: "UPDATE_USER",
      payload: {
        user: updatedUser,
      },
    });
  }, []);

  const addPaymentMethod = useCallback(
    (newPaymentMethod) => {
      const updatedPaymentMethods = [...state.paymentMethods, newPaymentMethod];
      dispatch({
        type: "UPDATE_PAYMENT_METHOD",
        payload: {
          paymentMethods: updatedPaymentMethods,
        },
      });
    },
    [state.paymentMethods],
  );

  const updatePaymentMethod = useCallback(
    (updatedPaymentMethod) => {
      dispatch({
        type: "UPDATE_PAYMENT_METHOD",
        payload: {
          paymentMethods: state.paymentMethods.map((method) =>
            method.id === updatedPaymentMethod.id ? updatedPaymentMethod : method,
          ),
        },
      });
    },
    [state.paymentMethods],
  );
  const deletePaymentMethod = useCallback(
    (paymentMethodId) => {
      const updatedPaymentMethods = state.paymentMethods.filter((method) => method.id !== paymentMethodId);
      dispatch({
        type: "UPDATE_PAYMENT_METHOD",
        payload: {
          paymentMethods: updatedPaymentMethods,
        },
      });
    },
    [state.paymentMethods],
  );

  const updateFranchise = useCallback((updateFranchise) => {
    dispatch({
      type: "UPDATE_FRANCHISE",
      payload: {
        franchiseId: updateFranchise,
      },
    });
  }, []);

  const memoizedValue = useMemo(
    () => ({
      isInitialized: state.isInitialized,
      isAuthenticated: state.isAuthenticated,
      user: state.user,
      availablePaymentOptions: state.paymentMethods,
      method: "jwt",
      login,
      register,
      logout,
      resetPassword,
      updateUser,
      addPaymentMethod,
      updatePaymentMethod,
      deletePaymentMethod,
      updateFranchise,
    }),
    [
      state.isAuthenticated,
      state.isInitialized,
      state.user,
      state.paymentMethods,
      login,
      logout,
      register,
      resetPassword,
      updateUser,
      addPaymentMethod,
      updatePaymentMethod,
      deletePaymentMethod,
      updateFranchise,
    ],
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}
